/**
 * Certificates Section Component
 * Admin management for certificates
 */

import { useEffect, lazy, Suspense } from 'react'
import { useDispatch, useSelector, useTranslation } from '@spcc/core'
import { useIsAdminReadOnly } from '@spcc/domains/auth'
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Button,
  Input,
  Badge,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  cn,
  Award,
  Plus,
  RefreshCw,
  Search,
  Pencil,
  Trash2,
  FileText,
} from '@spcc/uikit'
import { certificatesActions } from '../../store/certificates.slice'
import {
  selectFilteredCertificates,
  selectCertificatesLoading,
  selectCertificatesFilter,
  selectCertificatesSaving,
} from '../../store/certificates.selectors'
import type { Certificate } from '../../infrastructure/api/certificates-api'
import { productsActions } from '../../store/products.slice'

const CertificateFormDrawer = lazy(() =>
  import('../drawers/certificate-form-drawer').then((m) => ({ default: m.CertificateFormDrawer }))
)

// ============================================================================
// Helper Functions
// ============================================================================

function getTypeLabel(cert: Certificate, t: (k: string) => string): { label: string; variant: 'default' | 'secondary' | 'outline' } {
  if (cert.isGeneral) return { label: t('products.certificates.typeGeneralLabel'), variant: 'default' }
  if (cert.variant) return { label: t('products.certificates.typeVariantLabel'), variant: 'secondary' }
  return { label: t('products.certificates.typeUnlinkedLabel'), variant: 'outline' }
}

function CertificateMobileCard({
  cert,
  t,
  saving,
  onEdit,
  onDelete,
}: {
  cert: Certificate
  t: (key: string) => string
  saving: boolean
  onEdit: (cert: Certificate) => void
  onDelete: (cert: Certificate) => void
}) {
  const typeInfo = getTypeLabel(cert, t)

  return (
    <div className="rounded-xl border bg-card/60 p-3">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <Award className="h-4 w-4 shrink-0 text-primary" />
            <p className="truncate text-sm font-semibold">{cert.title}</p>
          </div>
          {cert.description ? (
            <p className="mt-1 line-clamp-2 text-xs text-muted-foreground">{cert.description}</p>
          ) : null}
        </div>
        <Badge variant={cert.isActive ? 'default' : 'outline'} className="shrink-0">
          {cert.isActive ? t('products.certificates.statusActive') : t('products.certificates.statusInactive')}
        </Badge>
      </div>

      <div className="mt-3 grid grid-cols-2 gap-3 text-sm">
        <div className="min-w-0">
          <p className="text-[10px] uppercase tracking-wider text-muted-foreground">{t('products.certificates.colType')}</p>
          <Badge variant={typeInfo.variant as any} className="mt-1 max-w-full">
            {typeInfo.label}
          </Badge>
        </div>
        <div className="min-w-0">
          <p className="text-[10px] uppercase tracking-wider text-muted-foreground">{t('products.certificates.colLinkedVariant')}</p>
          <p className="mt-1 truncate text-xs text-muted-foreground">
            {cert.variant
              ? (typeof cert.variant.name === 'string' ? cert.variant.name : cert.variant.documentId)
              : '—'}
          </p>
        </div>
      </div>

      <div className="mt-3 flex items-center justify-between gap-3">
        {cert.file ? (
          <a
            href={cert.file.url}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex min-w-0 items-center gap-1.5 text-xs text-primary hover:underline"
          >
            <FileText className="h-3.5 w-3.5 shrink-0" />
            <span className="truncate">{cert.file.name}</span>
          </a>
        ) : (
          <span className="text-xs text-muted-foreground/60">{t('products.certificates.noFile')}</span>
        )}
        <div className="flex shrink-0 items-center gap-1">
          <Button size="icon" variant="ghost" className="h-9 w-9" onClick={() => onEdit(cert)}>
            <Pencil className="h-3.5 w-3.5" />
          </Button>
          <Button
            size="icon"
            variant="ghost"
            className="h-9 w-9 text-destructive hover:bg-destructive/10"
            onClick={() => onDelete(cert)}
            disabled={saving}
          >
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>
    </div>
  )
}

// ============================================================================
// Component
// ============================================================================

export function CertificatesSection() {
  const dispatch = useDispatch()
  const { t } = useTranslation()
  const isReadOnly = useIsAdminReadOnly()
  const certs = useSelector(selectFilteredCertificates)
  const loading = useSelector(selectCertificatesLoading)
  const saving = useSelector(selectCertificatesSaving)
  const filter = useSelector(selectCertificatesFilter)

  useEffect(() => {
    dispatch(certificatesActions.fetchCertificates())
    dispatch(productsActions.fetchProducts())
  }, [dispatch])

  const handleAdd = () => dispatch(certificatesActions.openCreateDrawer())
  const handleEdit = (cert: Certificate) => dispatch(certificatesActions.openEditDrawer(cert))
  const handleDelete = (cert: Certificate) => {
    if (window.confirm(t('products.certificates.deleteConfirm').replace('$1', cert.title))) {
      dispatch(certificatesActions.deleteCertificate(cert.documentId))
    }
  }
  const handleRefresh = () => dispatch(certificatesActions.fetchCertificates())

  return (
    <div className="space-y-4 p-0 sm:space-y-6 sm:p-6">
      {/* Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex min-w-0 items-center gap-2">
          <Award className="h-6 w-6 text-primary" />
          <h1 className="truncate text-2xl font-bold text-foreground sm:text-3xl">{t('products.certificates.title')}</h1>
          <Badge variant="secondary">{certs.length}</Badge>
        </div>
        <div className="grid grid-cols-[1fr_1fr] gap-2 sm:flex sm:justify-end">
          <Button variant="outline" size="sm" className="h-11 sm:h-9" onClick={handleRefresh} disabled={loading}>
            <RefreshCw className={cn('h-4 w-4 me-1.5', loading && 'animate-spin')} />
            {t('products.certificates.refresh')}
          </Button>
          {!isReadOnly && (
            <Button size="sm" className="h-11 min-w-0 sm:h-9" onClick={handleAdd}>
              <Plus className="h-4 w-4 me-1.5" />
              <span className="truncate">{t('products.certificates.addCertificate')}</span>
            </Button>
          )}
        </div>
      </div>

      {/* Filters */}
      <Card className="min-w-0">
        <CardContent className="grid gap-3 pt-4 pb-4 sm:grid-cols-[1fr_auto_auto]">
          <div className="relative min-w-0">
            <Search className="absolute start-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
            <Input
              placeholder={t('products.certificates.searchPlaceholder')}
              className="ps-9"
              value={filter.search}
              onChange={(e) => dispatch(certificatesActions.setFilter({ search: e.target.value }))}
            />
          </div>

          <Select
            value={filter.type}
            onValueChange={(v) => dispatch(certificatesActions.setFilter({ type: v as typeof filter.type }))}
          >
            <SelectTrigger className="h-11 w-full sm:h-9 sm:w-36">
              <SelectValue placeholder={t('products.certificates.typeFilter')} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t('products.certificates.allTypes')}</SelectItem>
              <SelectItem value="general">{t('products.certificates.typeGeneral')}</SelectItem>
              <SelectItem value="variant">{t('products.certificates.typeVariant')}</SelectItem>
            </SelectContent>
          </Select>

          <Select
            value={filter.isActive}
            onValueChange={(v) => dispatch(certificatesActions.setFilter({ isActive: v as typeof filter.isActive }))}
          >
            <SelectTrigger className="h-11 w-full sm:h-9 sm:w-32">
              <SelectValue placeholder={t('products.certificates.colStatus')} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t('products.certificates.allStatuses')}</SelectItem>
              <SelectItem value="true">{t('products.certificates.statusActive')}</SelectItem>
              <SelectItem value="false">{t('products.certificates.statusInactive')}</SelectItem>
            </SelectContent>
          </Select>
        </CardContent>
      </Card>

      {/* Table */}
      <Card className="min-w-0 overflow-hidden">
        <CardContent className="p-0">
          {loading ? (
            <div className="p-6 space-y-3">
              {Array.from({ length: 5 }).map((_, i) => (
                <Skeleton key={i} className="h-12 w-full rounded-md" />
              ))}
            </div>
          ) : certs.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-muted-foreground gap-2">
              <Award className="h-10 w-10 opacity-30" />
              <p className="text-sm">{t('products.certificates.noCertificates')}</p>
              {!isReadOnly && (
                <Button size="sm" variant="outline" onClick={handleAdd}>
                  <Plus className="h-3.5 w-3.5 me-1.5" />
                  {t('products.certificates.addFirstCertificate')}
                </Button>
              )}
            </div>
          ) : (
            <>
              <div className="space-y-3 p-3 md:hidden">
                {certs.map((cert) => (
                  <CertificateMobileCard
                    key={cert.documentId}
                    cert={cert}
                    t={t}
                    saving={saving}
                    onEdit={handleEdit}
                    onDelete={handleDelete}
                  />
                ))}
              </div>
              <div className="hidden overflow-x-auto md:block">
                <Table className="min-w-[820px]">
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t('products.certificates.colTitle')}</TableHead>
                      <TableHead>{t('products.certificates.colType')}</TableHead>
                      <TableHead>{t('products.certificates.colLinkedVariant')}</TableHead>
                      <TableHead>{t('products.certificates.colFile')}</TableHead>
                      <TableHead>{t('products.certificates.colStatus')}</TableHead>
                      <TableHead className="text-end">{t('products.certificates.colActions')}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {certs.map((cert) => {
                      const typeInfo = getTypeLabel(cert, t)
                      return (
                        <TableRow key={cert.documentId}>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <Award className="h-4 w-4 text-primary shrink-0" />
                              <div>
                                <p className="font-medium text-sm">{cert.title}</p>
                                {cert.description && (
                                  <p className="text-xs text-muted-foreground truncate max-w-xs">{cert.description}</p>
                                )}
                              </div>
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge variant={typeInfo.variant as any}>{typeInfo.label}</Badge>
                          </TableCell>
                          <TableCell className="text-sm text-muted-foreground">
                            {cert.variant
                              ? (typeof cert.variant.name === 'string' ? cert.variant.name : cert.variant.documentId)
                              : <span className="text-muted-foreground/50">—</span>}
                          </TableCell>
                          <TableCell>
                            {cert.file ? (
                              <a
                                href={cert.file.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-1.5 text-xs text-primary hover:underline"
                              >
                                <FileText className="h-3.5 w-3.5" />
                                {cert.file.name}
                              </a>
                            ) : (
                              <span className="text-muted-foreground/50 text-xs">{t('products.certificates.noFile')}</span>
                            )}
                          </TableCell>
                          <TableCell>
                            <Badge variant={cert.isActive ? 'default' : 'outline'}>
                              {cert.isActive ? t('products.certificates.statusActive') : t('products.certificates.statusInactive')}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-end">
                            <div className="flex items-center justify-end gap-1">
                              <Button
                                size="icon"
                                variant="ghost"
                                className="h-8 w-8"
                                onClick={() => handleEdit(cert)}
                              >
                                <Pencil className="h-3.5 w-3.5" />
                              </Button>
                              <Button
                                size="icon"
                                variant="ghost"
                                className="h-8 w-8 text-destructive hover:bg-destructive/10"
                                onClick={() => handleDelete(cert)}
                                disabled={saving}
                              >
                                <Trash2 className="h-3.5 w-3.5" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      )
                    })}
                  </TableBody>
                </Table>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      <Suspense fallback={null}>
        <CertificateFormDrawer />
      </Suspense>
    </div>
  )
}
